import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useForm, Controller } from "react-hook-form";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Area,
  AreaChart,
  BarChart,
  Bar,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Header } from "@/components/header";
import { InvestmentPlanPDF } from "@/components/InvestmentPlanPDF";
import { InvestorReportPDF } from "@/components/InvestorReportPDF";
import GlobalSettings from "@/components/GlobalSettings";
import { Skeleton } from "@/components/ui/skeleton";
import OperatingCostsManager from "@/components/OperatingCostsManager";
import { useAppStore, useInvestmentData, useCostData, useTeamData, useNavigation, type AppState, type Currency, CURRENCY_RATES } from "../stores/appStore";
import { useToast } from "@/hooks/use-toast";
import { pdf } from "@react-pdf/renderer";
import html2canvas from "html2canvas";
import {
  Copy,
  FileText,
  TrendingUp,
  Calendar,
  DollarSign,
  Target,
   Link,
   X,
   Users,
   BarChart3,
   Download,
 } from "lucide-react";

// Import components normally to avoid hydration issues
import CostBreakdown from "./CostBreakdown";
import JobsAndPeople from "./JobsAndPeople";
import UnitEconomicsSimulator from "./UnitEconomicsSimulator";
import CalculationErrorBoundary from "./CalculationErrorBoundary";
import ProjectOverview from "./ProjectOverview";
import { useProjectMetrics } from "@/hooks/useProjectMetrics";

// Custom hook for data synchronization with visual feedback
function useDataSync<T>(
  contextValue: T,
  updateContext: (updates: Partial<T>) => void,
  toast: any,
) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);

  // Sync function with visual feedback
  const syncToContext = useCallback(
    async (updates: Partial<T>, successMessage?: string) => {
      setIsSyncing(true);
      try {
        // Update context
        updateContext(updates);

        // Show success feedback
        setLastSynced(new Date());

        if (successMessage) {
          toast({
            title: "Updated",
            description: successMessage,
          });
        }

        // Small delay for visual feedback
        setTimeout(() => setIsSyncing(false), 500);
      } catch (error) {
        console.error("Sync failed:", error);
        setIsSyncing(false);
        toast({
          title: "Sync Failed",
          description: "Failed to save changes. Please try again.",
          variant: "destructive",
        });
      }
    },
    [updateContext, toast],
  );

  return {
    syncToContext,
    isSyncing,
    lastSynced,
  };
}

// Form data type
type InvestmentFormData = {
  valuationType: string;
  investmentAmount: number;
  equityPercentage: number;
  revenueMultiple: number;
  soloPrice: number;
  teamPrice: number;
  unlimitedPrice: number;
  initialUsers: number;
  monthlyGrowthRate: number;
  monthlyOpex: number;
  enableExpenseGrowth: boolean;
  expenseGrowthRate: number;
  expenseGrowthCap: number;
  soloGrowthRate: number;
  teamGrowthRate: number;
  unlimitedGrowthRate: number;
};

export default function SaaSInvestmentCalculator() {
  // Prevent SSR issues
  if (typeof window === "undefined") return null;

  const state = useAppStore();
  const { setValuationType } = state;
  const { data: investmentData, setInvestmentData } = useInvestmentData();
  const { activeTab: mainTab, setActiveTab: setMainTab } = useNavigation();
  const { currency } = state;
  const { toast } = useToast();

  // State for UI feedback
  const [showCopied, setShowCopied] = useState(false);
  const [showUrlCopied, setShowUrlCopied] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<'investment' | 'growth' | 'costs'>('investment');

  // Load startup data if startupId is present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const startupId = urlParams.get('startupId');

    if (startupId) {
      const savedStartups = localStorage.getItem('userStartups');
      if (savedStartups) {
        try {
          const startups = JSON.parse(savedStartups);
          const startup = startups.find((s: any) => s.id === startupId);

          if (startup) {
            // Update project name
            state.setProjectData({
              projectName: startup.name,
              description: startup.description
            });

            // Load startup data into forms if available
            if (startup.data) {
              if (startup.data.companyProfile) {
                // Update company profile data
                setInvestmentData({
                  ...investmentData,
                  ...startup.data.companyProfile
                });
              }

              // Load business configuration
              if (startup.data.currency) {
                state.setCurrency(startup.data.currency);
              }
              if (startup.data.country) {
                state.setCountry(startup.data.country);
              }
              if (startup.data.soloPrice) {
                setInvestmentData({ ...investmentData, soloPrice: startup.data.soloPrice });
              }
              if (startup.data.teamPrice) {
                setInvestmentData({ ...investmentData, teamPrice: startup.data.teamPrice });
              }
              if (startup.data.unlimitedPrice) {
                setInvestmentData({ ...investmentData, unlimitedPrice: startup.data.unlimitedPrice });
              }

              if (startup.data.investmentScenario) {
                // Update investment scenario data
                setInvestmentData({
                  ...investmentData,
                  ...startup.data.investmentScenario
                });
              }

              if (startup.data.teamRoles) {
                // Update team roles
                state.setTeamRoles(startup.data.teamRoles);
              }

              if (startup.data.operatingCosts) {
                // Update operating costs
                state.setOperatingCosts(startup.data.operatingCosts);
              }
            }
          }
        } catch (error) {
          console.error('Error loading startup data:', error);
        }
      }
    }

    // Load onboarding investment data if available
    const onboardingData = localStorage.getItem('onboardingInvestmentData');
    if (onboardingData) {
      try {
        const investmentData = JSON.parse(onboardingData);
        setInvestmentData({
          ...state.investmentData,
          ...investmentData
        });
        // Clean up the temporary data
        localStorage.removeItem('onboardingInvestmentData');
      } catch (error) {
        console.error('Error loading onboarding investment data:', error);
      }
    }
  }, []);



  // Helper functions for currency conversion in pricing inputs
  const convertToINR = (value: number) => value / CURRENCY_RATES[currency]; // Convert from current currency to INR
  const convertFromINR = (value: number) => value * CURRENCY_RATES[currency]; // Convert from INR to current currency

    // Form setup
    const { control, watch, reset } = useForm<InvestmentFormData>({
      defaultValues: {
        ...investmentData,
        valuationType: state.valuationType,
      },
    });

    const formValues = watch();

    // Auto-save form data to startup when it changes
    useEffect(() => {
      if (formValues) {
        const urlParams = new URLSearchParams(window.location.search);
        const startupId = urlParams.get('startupId');

        if (startupId) {
          const savedStartups = localStorage.getItem('userStartups');
          if (savedStartups) {
            try {
              const startups = JSON.parse(savedStartups);
              const startupIndex = startups.findIndex((s: any) => s.id === startupId);

              if (startupIndex >= 0) {
                // Update startup with current form data
                startups[startupIndex] = {
                  ...startups[startupIndex],
                  updatedAt: new Date(),
                  data: {
                    ...startups[startupIndex].data,
                    investmentScenario: {
                      investmentAmount: formValues.investmentAmount,
                      equityPercentage: formValues.equityPercentage,
                      seekingInvestment: true, // Assume seeking if in calculator
                    },
                    companyProfile: {
                      name: state.projectName,
                      description: state.description,
                      industry: state.industry,
                      location: state.country,
                    }
                  }
                };
                localStorage.setItem('userStartups', JSON.stringify(startups));
              }
            } catch (error) {
              console.error('Error auto-saving startup data:', error);
            }
          }
        }
      }
    }, [formValues, state.projectName, state.description]);

    // Helper functions for header actions
    const getCurrentConfig = () => {
      return {
        projectName: state.projectName,
        valuationType: state.valuationType,
        investmentAmount: state.investmentData.investmentAmount,
        equityPercentage: state.investmentData.equityPercentage,
        soloPrice: state.investmentData.soloPrice,
        teamPrice: state.investmentData.teamPrice,
        unlimitedPrice: state.investmentData.unlimitedPrice,
        monthlyOpex: state.investmentData.monthlyOpex,
        initialUsers: state.investmentData.initialUsers,
        monthlyGrowthRate: state.investmentData.monthlyGrowthRate,
        revenueMultiple: state.investmentData.revenueMultiple,
        soloGrowthRate: state.investmentData.soloGrowthRate,
        teamGrowthRate: state.investmentData.teamGrowthRate,
        unlimitedGrowthRate: state.investmentData.unlimitedGrowthRate,
        enableExpenseGrowth: state.investmentData.enableExpenseGrowth,
        expenseGrowthRate: state.investmentData.expenseGrowthRate,
        expenseGrowthCap: state.investmentData.expenseGrowthCap,
        currency: state.currency,
        country: state.country,
        planningMonths: state.planningMonths,
      };
    };

    const copyConfigToClipboard = () => {
      const config = getCurrentConfig();
      navigator.clipboard.writeText(JSON.stringify(config, null, 2));
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    };

    const updateURL = () => {
      const config = getCurrentConfig();
      const urlParams = new URLSearchParams();

      // Add all config parameters to URL
      Object.entries(config).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          urlParams.set(key, String(value));
        }
      });

      // Add current theme to URL
      const currentTheme = localStorage.getItem("saas-invest-theme") || "dark";
      urlParams.set("theme", currentTheme);

      const newURL = `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, "", newURL);
    };

    const copyURL = () => {
      updateURL(); // First update the URL
      const currentURL = window.location.href;
      navigator.clipboard.writeText(currentURL);
      setShowUrlCopied(true);
      setTimeout(() => setShowUrlCopied(false), 2000);
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const config = JSON.parse(e.target?.result as string);
            // Load the configuration
            if (config.projectName) state.setProjectData({ projectName: config.projectName });
            if (config.valuationType) state.setValuationType(config.valuationType);
            if (config.currency) state.setCurrency(config.currency);
            if (config.country) state.setCountry(config.country);
            if (config.planningMonths) state.setPlanningMonths(config.planningMonths);
            if (config.investmentData) setInvestmentData(config.investmentData);

            toast({
              title: "Configuration loaded",
              description: "Settings have been restored from the file.",
            });
          } catch (error) {
            toast({
              title: "Error loading configuration",
              description: "The file could not be parsed. Please check the format.",
              variant: "destructive",
            });
          }
        };
        reader.readAsText(file);
      }
    };

    const handleMainTabChange = (tab: AppState['activeTab']) => {
      setMainTab(tab);
      // Update URL
      const url = new URL(window.location.href);
      if (tab === 'costing') {
        url.searchParams.set('tab', 'costing');
      } else if (tab === 'people') {
        url.searchParams.set('tab', 'people');
      } else {
        url.searchParams.delete('tab');
      }
      window.history.replaceState({}, '', url.toString());
    };

    const handleProjectNameChange = (name: string) => {
      state.setProjectData({ projectName: name });

      // Sync with localStorage if we have a startupId
      const urlParams = new URLSearchParams(window.location.search);
      const startupId = urlParams.get('startupId');
      if (startupId) {
        const savedStartups = localStorage.getItem('userStartups');
        if (savedStartups) {
          try {
            const startups = JSON.parse(savedStartups);
            const startupIndex = startups.findIndex((s: any) => s.id === startupId);
            if (startupIndex >= 0) {
              startups[startupIndex] = {
                ...startups[startupIndex],
                name: name,
                updatedAt: new Date(),
              };
              localStorage.setItem('userStartups', JSON.stringify(startups));
            }
          } catch (error) {
            console.error('Error updating startup name:', error);
          }
        }
      }
    };

    // Get team roles and operating costs from store (needed for calculations)
    const teamRoles = state.teamRoles || [];
    const operatingCosts = state.operatingCosts || [];

    // Use the metrics hook with formValues for real-time updates
    const {
      projectionData,
      breakEvenMonthValue,
      finalMetrics,
      companyValuation,
      investorEquityValue,
      roi,
      startupMetrics,
      formatCurrency
    } = useProjectMetrics(formValues);

    // Chart refs
    const cashChartRef = useRef<HTMLDivElement>(null);
    const revenueChartRef = useRef<HTMLDivElement>(null);
    const expensesChartRef = useRef<HTMLDivElement>(null);
    const usersChartRef = useRef<HTMLDivElement>(null);

    // Handle valuation change
    const handleValuationChange = (type: string) => {
      // Currency-aware valuation presets with realistic pre-seed/seed data
      const presets: Record<string, { investmentAmount: number; equityPercentage: number; revenueMultiple: number; label: string }> = {
        custom: { investmentAmount: 0, equityPercentage: 0, revenueMultiple: 1, label: "Custom" },

        // Popular Accelerators & Programs (Pre-seed/Seed)
        yc: {
          investmentAmount: currency === 'USD' ? 150000 : currency === 'AED' ? 550500 : 12000000,
          equityPercentage: 7,
          revenueMultiple: 4,
          label: `Y Combinator (${7}% for ${currency === 'USD' ? '$150K' : currency === 'AED' ? 'د.إ550K' : '₹1.2Cr'})`
        },
        '500_startups': {
          investmentAmount: currency === 'USD' ? 125000 : currency === 'AED' ? 458750 : 10000000,
          equityPercentage: 6,
          revenueMultiple: 5,
          label: `500 Startups (${6}% for ${currency === 'USD' ? '$125K' : currency === 'AED' ? 'د.إ459K' : '₹1Cr'})`
        },
        techstars: {
          investmentAmount: currency === 'USD' ? 100000 : currency === 'AED' ? 367000 : 8000000,
          equityPercentage: 6,
          revenueMultiple: 4,
          label: `Techstars (${6}% for ${currency === 'USD' ? '$100K' : currency === 'AED' ? 'د.إ367K' : '₹80L'})`
        },

        // Popular Angel Networks (Pre-seed/Seed)
        angelList: {
          investmentAmount: currency === 'USD' ? 100000 : currency === 'AED' ? 367000 : 8000000,
          equityPercentage: 10,
          revenueMultiple: 5,
          label: `AngelList (${10}% for ${currency === 'USD' ? '$100K' : currency === 'AED' ? 'د.إ367K' : '₹80L'})`
        },
        golden_gate_ventures: {
          investmentAmount: currency === 'USD' ? 250000 : currency === 'AED' ? 917500 : 20000000,
          equityPercentage: 8,
          revenueMultiple: 6,
          label: `Golden Gate Ventures (${8}% for ${currency === 'USD' ? '$250K' : currency === 'AED' ? 'د.إ918K' : '₹2Cr'})`
        },

        // Indian Angel Networks (Pre-seed/Seed)
        indian_angel_network: {
          investmentAmount: currency === 'INR' ? 7500000 : currency === 'AED' ? 275250 : 93750,
          equityPercentage: 6,
          revenueMultiple: 9,
          label: `Indian Angel Network (${6}% for ₹75L)`
        },
        Chennai_angels: {
          investmentAmount: currency === 'INR' ? 5000000 : currency === 'AED' ? 183500 : 62500,
          equityPercentage: 5,
          revenueMultiple: 8,
          label: `Chennai Angels (${5}% for ₹50L)`
        },

        // Early Stage VC Firms (Pre-seed/Seed focus)
        '100x': {
          investmentAmount: currency === 'USD' ? 500000 : currency === 'AED' ? 1835000 : 40000000,
          equityPercentage: 8,
          revenueMultiple: 12,
          label: `100x.VC (${8}% for ${currency === 'USD' ? '$500K' : currency === 'AED' ? 'د.إ1.8M' : '₹4Cr'})`
        },
        better_capital: {
          investmentAmount: currency === 'USD' ? 3000000 : currency === 'AED' ? 11010000 : 240000000,
          equityPercentage: 12,
          revenueMultiple: 14,
          label: `Better Capital (${12}% for ${currency === 'USD' ? '$3M' : currency === 'AED' ? 'د.إ11M' : '₹24Cr'})`
        },
        peak_xv: {
          investmentAmount: currency === 'USD' ? 4000000 : currency === 'AED' ? 14680000 : 320000000,
          equityPercentage: 11,
          revenueMultiple: 15,
          label: `Peak XV (${11}% for ${currency === 'USD' ? '$4M' : currency === 'AED' ? 'د.إ14.7M' : '₹32Cr'})`
        },

        // Indian Early Stage Funds
        kalaari_capital: {
          investmentAmount: currency === 'USD' ? 3000000 : currency === 'AED' ? 11010000 : 240000000,
          equityPercentage: 12,
          revenueMultiple: 12,
          label: `Kalaari Capital (${12}% for ${currency === 'USD' ? '$3M' : currency === 'AED' ? 'د.إ11M' : '₹24Cr'})`
        },
        matrix_partners_india: {
          investmentAmount: currency === 'USD' ? 2500000 : currency === 'AED' ? 9175000 : 200000000,
          equityPercentage: 15,
          revenueMultiple: 10,
          label: `Matrix Partners India (${15}% for ${currency === 'USD' ? '$2.5M' : currency === 'AED' ? 'د.إ9.2M' : '₹20Cr'})`
        },
        india_quotient: {
          investmentAmount: currency === 'USD' ? 3500000 : currency === 'AED' ? 12845000 : 280000000,
          equityPercentage: 11,
          revenueMultiple: 13,
          label: `India Quotient (${11}% for ${currency === 'USD' ? '$3.5M' : currency === 'AED' ? 'د.إ12.8M' : '₹28Cr'})`
        },
        nexus_venture_partners: {
          investmentAmount: currency === 'USD' ? 2000000 : currency === 'AED' ? 7340000 : 160000000,
          equityPercentage: 13,
          revenueMultiple: 11,
          label: `Nexus Venture Partners (${13}% for ${currency === 'USD' ? '$2M' : currency === 'AED' ? 'د.إ7.3M' : '₹16Cr'})`
        },

        // US/European Early Stage Funds
        benchmark: {
          investmentAmount: currency === 'USD' ? 6000000 : currency === 'AED' ? 22020000 : 480000000,
          equityPercentage: 10,
          revenueMultiple: 16,
          label: `Benchmark (${10}% for ${currency === 'USD' ? '$6M' : currency === 'AED' ? 'د.إ22M' : '₹48Cr'})`
        },
        balderton: {
          investmentAmount: currency === 'USD' ? 7000000 : currency === 'AED' ? 25710000 : 560000000,
          equityPercentage: 11,
          revenueMultiple: 17,
          label: `Balderton Capital (${11}% for ${currency === 'USD' ? '$7M' : currency === 'AED' ? 'د.إ25.7M' : '₹56Cr'})`
        },

        // Generic seed stages (for reference)
        seed: {
          investmentAmount: currency === 'USD' ? 500000 : currency === 'AED' ? 1835000 : 40000000,
          equityPercentage: 20,
          revenueMultiple: 6,
          label: `Seed Stage (${20}% for ${currency === 'USD' ? '$500K' : currency === 'AED' ? 'د.إ1.8M' : '₹4Cr'})`
        },
        pre_seed: {
          investmentAmount: currency === 'USD' ? 250000 : currency === 'AED' ? 917500 : 20000000,
          equityPercentage: 15,
          revenueMultiple: 8,
          label: `Pre-seed (${15}% for ${currency === 'USD' ? '$250K' : currency === 'AED' ? 'د.إ918K' : '₹2Cr'})`
        }
      };

      const preset = presets[type] || presets.custom;

      // Update form values with preset values (values are already in target currency, convert to INR for storage)
      const investmentAmountINR = convertToINR(preset.investmentAmount);
      const equityPercentage = preset.equityPercentage;
      const revenueMultiple = preset.revenueMultiple;

      // Update the store
      setInvestmentData({
        ...investmentData,
        investmentAmount: investmentAmountINR,
        equityPercentage: equityPercentage,
        revenueMultiple: revenueMultiple,
        valuationType: type,
      });
      setValuationType(type);

      // Update the form values
      reset({
        ...investmentData,
        investmentAmount: investmentAmountINR,
        equityPercentage: equityPercentage,
        revenueMultiple: revenueMultiple,
        valuationType: type,
      });
    };

    const generatePDF = async () => {
     try {
        const doc = (
          <InvestmentPlanPDF
            projectName={state.projectName}
            valuationType={formValues.valuationType || 'custom'}
            investmentAmount={formValues.investmentAmount || 0}
            equityPercentage={formValues.equityPercentage || 0}
            soloPrice={formValues.soloPrice || 0}
            teamPrice={formValues.teamPrice || 0}
            unlimitedPrice={formValues.unlimitedPrice || 0}
            monthlyOpex={formValues.monthlyOpex || 0}
            initialUsers={formValues.initialUsers || 0}
            monthlyGrowthRate={formValues.monthlyGrowthRate || 0}
            revenueMultiple={formValues.revenueMultiple || 1}
            soloGrowthRate={formValues.soloGrowthRate || 0}
            teamGrowthRate={formValues.teamGrowthRate || 0}
            unlimitedGrowthRate={formValues.unlimitedGrowthRate || 0}
            projectionData={projectionData}
            breakEvenMonth={breakEvenMonthValue}
            finalMetrics={finalMetrics}
            companyValuation={companyValuation}
            investorEquityValue={investorEquityValue}
            roi={roi}
            formatCurrency={formatCurrency}
          />
        );

       const blob = await pdf(doc).toBlob();
       const url = URL.createObjectURL(blob);
       const a = document.createElement('a');
       a.href = url;
       a.download = `${state.projectName}-investment-plan.pdf`;
       document.body.appendChild(a);
       a.click();
       document.body.removeChild(a);
       URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF. Please try again.');
      }
    };

    const generateInvestorReportPDF = async () => {
      try {
        // Capture chart images for the PDF
        const chartImages: any = {};

        if (cashChartRef.current) {
          const cashCanvas = await html2canvas(cashChartRef.current);
          chartImages.cashChart = cashCanvas.toDataURL('image/png');
        }

        if (revenueChartRef.current) {
          const revenueCanvas = await html2canvas(revenueChartRef.current);
          chartImages.revenueChart = revenueCanvas.toDataURL('image/png');
        }

        if (expensesChartRef.current) {
          const expensesCanvas = await html2canvas(expensesChartRef.current);
          chartImages.expensesChart = expensesCanvas.toDataURL('image/png');
        }

        if (usersChartRef.current) {
          const usersCanvas = await html2canvas(usersChartRef.current);
          chartImages.usersChart = usersCanvas.toDataURL('image/png');
        }

        const doc = (
          <InvestorReportPDF
            projectName={state.projectName}
            currency={currency}
            investmentData={formValues}
            teamRoles={teamRoles}
            operatingCosts={operatingCosts}
            projectionData={projectionData}
            breakEvenMonth={breakEvenMonthValue}
            finalMetrics={finalMetrics}
            companyValuation={companyValuation}
            investorEquityValue={investorEquityValue}
            roi={roi}
            formatCurrency={formatCurrency}
            chartImages={chartImages}
          />
        );

        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${state.projectName}-investor-report.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error generating investor report PDF:', error);
        alert('Error generating investor report PDF. Please try again.');
      }
    };

    const exportChartAsPNG = async (chartRef: React.RefObject<HTMLDivElement>, chartName: string) => {
      if (!chartRef.current) return;

      try {
        const canvas = await html2canvas(chartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2, // Higher resolution
        });
        const link = document.createElement('a');
        link.download = `${state.projectName}-${chartName}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (error) {
        console.error('Error exporting chart:', error);
        alert('Error exporting chart. Please try again.');
      }
    };

    const MetricCard = ({ 
      title, 
      value, 
      icon: Icon, 
      trend, 
      trendLabel,
      colorClass = "text-primary"
    }: { 
      title: string, 
      value: string | number, 
      icon: any, 
      trend?: string,
      trendLabel?: string,
      colorClass?: string
    }) => (
      <Card className="group relative overflow-hidden border-border/60 bg-card/50 hover:bg-card hover:border-border transition-all duration-300 shadow-sm hover:shadow-md">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className={`p-2 rounded-lg bg-background border border-border/50 group-hover:border-${colorClass.replace('text-', '')}/30 group-hover:bg-${colorClass.replace('text-', '')}/5 transition-colors duration-300`}>
              <Icon className={`w-5 h-5 ${colorClass}`} />
            </div>
            {trend && (
              <div className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 rounded-full">
                <TrendingUp className="w-3 h-3" />
                {trend}
              </div>
            )}
          </div>
          
          <div className="space-y-1">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</h3>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold tracking-tight text-foreground">{value}</span>
            </div>
            {trendLabel && (
              <p className="text-xs text-muted-foreground/80 mt-1.5 flex items-center gap-1.5">
                <span className="inline-block w-1 h-1 rounded-full bg-border"></span>
                {trendLabel}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );

   return (
    <div className="min-h-screen bg-background">
        <Header
            showHero={mainTab === 'overview' || mainTab === 'calculator'}
            title={
              mainTab === 'overview' ? "Project Overview" :
              mainTab === 'calculator' ? "Investment Analysis" :
              mainTab === 'unit-economics' ? "Unit Economics Analysis" :
              "Cost Analysis"
            }
             description={
               mainTab === 'overview' ? "Quick summary and project details" :
               mainTab === 'calculator' ? "Comprehensive financial projections and valuation analysis" :
               mainTab === 'unit-economics' ? "Strategic financial modeling for customer profitability and growth" :
               "22-month financial planning for SaaS startups"
             }
           onCopyConfig={mainTab === 'calculator' ? copyConfigToClipboard : undefined}
           onCopyURL={mainTab === 'calculator' ? copyURL : undefined}
            onGeneratePDF={mainTab === 'calculator' ? generatePDF : undefined}
            onGenerateInvestorReportPDF={mainTab === 'calculator' ? generateInvestorReportPDF : undefined}
           onLoadConfig={mainTab === 'calculator' ? handleFileUpload : undefined}
           showCopied={showCopied}
           showUrlCopied={showUrlCopied}
            onOpenSettings={mainTab === 'calculator' ? () => setIsSidebarOpen(true) : undefined}
            onOpenGlobalSettings={() => setShowGlobalSettings(true)}
           onProjectNameChange={handleProjectNameChange}
            projectName={state.projectName}
           investmentAmount={mainTab === 'calculator' ? formValues.investmentAmount : undefined}
           currentValuation={mainTab === 'calculator' ? companyValuation : undefined}
           roi={mainTab === 'calculator' ? roi : undefined}
           breakEvenMonth={mainTab === 'calculator' ? (breakEvenMonthValue ?? undefined) : undefined}
           isSyncing={isSyncing}
           lastSynced={lastSynced}
           activeTab={mainTab}
           onTabChange={handleMainTabChange}
        />

      <div className="container mx-auto px-6 py-8">
        {mainTab === 'overview' ? (
          <ProjectOverview />
        ) : mainTab === 'calculator' ? (
          <div className="space-y-8 animate-in fade-in duration-500">
             {/* Key Metrics Section */}
             <CalculationErrorBoundary>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 px-1">
                      <Target className="w-4 h-4 text-primary" />
                      <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Key Financial Indicators</h2>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard 
                      title="3-Year ROI" 
                      value={`${roi.toFixed(1)}%`} 
                      icon={TrendingUp} 
                      colorClass="text-emerald-500"
                      trendLabel="Return on Investment"
                    />
                    <MetricCard 
                      title="Current Valuation" 
                      value={formatCurrency(companyValuation || 0)} 
                      icon={DollarSign} 
                      colorClass="text-purple-500"
                      trend={`${formValues.revenueMultiple}x`}
                      trendLabel="Revenue Multiple"
                    />
                    <MetricCard 
                      title="Break Even" 
                      value={breakEvenMonthValue ? `Month ${breakEvenMonthValue}` : "N/A"} 
                      icon={Calendar} 
                      colorClass="text-blue-500"
                      trendLabel="Cash flow positive"
                    />
                    <MetricCard 
                      title="Runway" 
                      value={startupMetrics.runwayMonths === 999 ? '∞' : `${startupMetrics.runwayMonths} Mo`} 
                      icon={Target} 
                      colorClass="text-orange-500"
                      trendLabel="Based on burn rate"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                     <MetricCard 
                      title="Monthly Recurring Revenue" 
                      value={formatCurrency(startupMetrics.mrr)} 
                      icon={DollarSign} 
                      colorClass="text-cyan-500"
                    />
                    <MetricCard 
                      title="Annual Recurring Revenue" 
                      value={formatCurrency(startupMetrics.arr)} 
                      icon={TrendingUp} 
                      colorClass="text-indigo-500"
                    />
                    <MetricCard 
                      title="Monthly Burn Rate" 
                      value={formatCurrency(startupMetrics.monthlyBurnRate)} 
                      icon={TrendingUp} 
                      colorClass="text-red-500"
                    />
                    <MetricCard 
                      title="Total Users (Year 3)" 
                      value={(finalMetrics?.users || 0).toLocaleString()} 
                      icon={Users} 
                      colorClass="text-pink-500"
                    />
                  </div>
                </div>
             </CalculationErrorBoundary>

         {/* Charts Section */}
         <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-border/40 pb-4 pt-4">
              <div className="flex items-center gap-2 px-1">
                <BarChart3 className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Financial Projections</h2>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
               <Card className="border-border/60 shadow-sm">
                 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                   <div className="space-y-1">
                     <CardTitle className="text-base font-medium">Cash Flow Projection</CardTitle>
                     <CardDescription>Cumulative cash position over 36 months</CardDescription>
                   </div>
                   <Button
                     variant="ghost"
                     size="sm"
                     onClick={() => exportChartAsPNG(cashChartRef, 'cash-flow')}
                     className="h-8 w-8 p-0"
                   >
                     <Download className="h-4 w-4 text-muted-foreground" />
                   </Button>
                 </CardHeader>
                <CardContent>
                  <div ref={cashChartRef}>
                    <ChartContainer
                      config={{
                        cumulativeCash: {
                          label: "Cumulative Cash",
                          color: "hsl(var(--chart-1))",
                        },
                      }}
                      className="w-full h-[300px]"
                    >
                      <AreaChart
                        data={projectionData}
                        margin={{ left: 0, right: 0, top: 10, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="cashGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-muted/30" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tickMargin={8}
                          tickFormatter={(value) => `M${value}`}
                          className="text-[10px] text-muted-foreground"
                        />
                         <YAxis
                           axisLine={false}
                           tickLine={false}
                           tickMargin={8}
                           tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                           className="text-[10px] text-muted-foreground"
                         />
                          <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent className="w-[180px]" />}
                           />
                        <Area
                          type="monotone"
                          dataKey="cumulativeCash"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#cashGradient)"
                        />
                      </AreaChart>
                    </ChartContainer>
                  </div>
                </CardContent>
              </Card>

               <Card className="border-border/60 shadow-sm">
                 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                   <div className="space-y-1">
                     <CardTitle className="text-base font-medium">Revenue vs Expenses</CardTitle>
                     <CardDescription>Monthly breakdown of income and costs</CardDescription>
                   </div>
                   <Button
                     variant="ghost"
                     size="sm"
                     onClick={() => exportChartAsPNG(expensesChartRef, 'revenue-expenses')}
                     className="h-8 w-8 p-0"
                   >
                     <Download className="h-4 w-4 text-muted-foreground" />
                   </Button>
                 </CardHeader>
                <CardContent>
                  <div ref={expensesChartRef}>
                     <ChartContainer
                        config={{
                           revenue: { label: "Revenue", color: "hsl(var(--chart-2))" },
                           expenses: { label: "Expenses", color: "hsl(var(--chart-1))" },
                        }}
                       className="w-full h-[300px]"
                     >
                        <LineChart
                          data={projectionData}
                          margin={{ left: 0, right: 0, top: 10, bottom: 0 }}
                        >
                         <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-muted/30" />
                         <XAxis
                           dataKey="month"
                           axisLine={false}
                           tickLine={false}
                           tickMargin={8}
                           tickFormatter={(value) => `M${value}`}
                           className="text-[10px] text-muted-foreground"
                         />
                         <YAxis
                           axisLine={false}
                           tickLine={false}
                           tickMargin={8}
                           tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                           className="text-[10px] text-muted-foreground"
                         />
                          <ChartTooltip content={<ChartTooltipContent className="w-[180px]" />} />
                           <Line
                             type="monotone"
                             dataKey="revenue"
                             stroke="hsl(var(--chart-2))"
                             strokeWidth={2}
                             dot={false}
                           />
                           <Line
                             type="monotone"
                             dataKey="expenses"
                             stroke="hsl(var(--chart-1))"
                             strokeWidth={2}
                             dot={false}
                           />
                        </LineChart>
                     </ChartContainer>
                   </div>
                 </CardContent>
              </Card>

              <Card className="border-border/60 shadow-sm">
                 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                   <div className="space-y-1">
                     <CardTitle className="text-base font-medium">User Growth</CardTitle>
                     <CardDescription>Total active users over time</CardDescription>
                   </div>
                   <Button
                     variant="ghost"
                     size="sm"
                     onClick={() => exportChartAsPNG(usersChartRef, 'user-growth')}
                     className="h-8 w-8 p-0"
                   >
                     <Download className="h-4 w-4 text-muted-foreground" />
                   </Button>
                 </CardHeader>
                <CardContent>
                  <div ref={usersChartRef}>
                    <ChartContainer
                      config={{
                        users: { label: "Users", color: "hsl(var(--chart-3))" },
                      }}
                      className="w-full h-[300px]"
                    >
                      <AreaChart
                        data={projectionData}
                        margin={{ left: 0, right: 0, top: 10, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--chart-3))" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="hsl(var(--chart-3))" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-muted/30" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tickMargin={8}
                          tickFormatter={(value) => `M${value}`}
                          className="text-[10px] text-muted-foreground"
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tickMargin={8}
                          tickFormatter={(value) => value.toLocaleString()}
                          className="text-[10px] text-muted-foreground"
                        />
                        <ChartTooltip content={<ChartTooltipContent className="w-[180px]" />} />
                        <Area
                          type="monotone"
                          dataKey="users"
                          stroke="hsl(var(--chart-3))"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#userGradient)"
                        />
                      </AreaChart>
                    </ChartContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/60 shadow-sm">
                 <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                   <div className="space-y-1">
                     <CardTitle className="text-base font-medium">Revenue Breakdown</CardTitle>
                     <CardDescription>Revenue by subscription tier</CardDescription>
                   </div>
                   <Button
                     variant="ghost"
                     size="sm"
                     onClick={() => exportChartAsPNG(revenueChartRef, 'revenue-breakdown')}
                     className="h-8 w-8 p-0"
                   >
                     <Download className="h-4 w-4 text-muted-foreground" />
                   </Button>
                 </CardHeader>
                <CardContent>
                  <div ref={revenueChartRef}>
                    <ChartContainer
                      config={{
                        soloRevenue: { label: "Solo", color: "hsl(var(--chart-4))" },
                        teamRevenue: { label: "Team", color: "hsl(var(--chart-5))" },
                        unlimitedRevenue: { label: "Unlimited", color: "hsl(var(--chart-2))" },
                      }}
                      className="w-full h-[300px]"
                    >
                      <BarChart
                        data={projectionData}
                        margin={{ left: 0, right: 0, top: 10, bottom: 0 }}
                      >
                        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-muted/30" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tickMargin={8}
                          tickFormatter={(value) => `M${value}`}
                          className="text-[10px] text-muted-foreground"
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tickMargin={8}
                          tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                          className="text-[10px] text-muted-foreground"
                        />
                         <ChartTooltip content={<ChartTooltipContent className="w-[180px]" />} />
                        <Bar dataKey="soloRevenue" stackId="a" fill="hsl(var(--chart-4))" radius={[0, 0, 4, 4]} />
                        <Bar dataKey="teamRevenue" stackId="a" fill="hsl(var(--chart-5))" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="unlimitedRevenue" stackId="a" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ChartContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
         </div>
          </div>
        ) : mainTab === 'unit-economics' ? (
          <UnitEconomicsSimulator />
        ) : mainTab === 'packages' ? (
          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-lg font-medium">Pricing & Revenue Model</h3>
              <p className="text-sm text-muted-foreground">
                Define your subscription pricing tiers and revenue structure
              </p>

              {/* Currency Selector */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <Label className="text-sm font-medium">Currency:</Label>
                <Select
                  value={currency}
                  onValueChange={(value: Currency) => state.setCurrency(value)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR (₹)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="AED">AED (د.إ)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This changes the global currency setting for the entire app
                </p>
              </div>
            </div>

            {/* Pricing Tiers */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                {/* Solo Tier */}
                <div className="p-4 border border-border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <h4 className="font-medium">Solo Tier</h4>
                  </div>
                   <div className="space-y-2">
                     <Label htmlFor="soloPrice" className="text-sm">Price per month ({currency === 'USD' ? '$' : currency === 'AED' ? 'د.إ' : '₹'})</Label>
                     <Controller
                       name="soloPrice"
                       control={control}
                       render={({ field }) => (
                         <Input
                           {...field}
                           value={field.value ? convertFromINR(field.value) : ''}
                           type="number"
                           placeholder={currency === 'USD' ? '12' : currency === 'AED' ? '44' : '99'}
                           className="h-10"
                           onChange={(e) => field.onChange(convertToINR(Number(e.target.value) || 0))}
                         />
                       )}
                     />
                  </div>
                </div>

                {/* Team Tier */}
                <div className="p-4 border border-border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <h4 className="font-medium">Team Tier</h4>
                  </div>
                  <div className="space-y-2">
                     <Label htmlFor="teamPrice" className="text-sm">Price per month ({currency === 'USD' ? '$' : currency === 'AED' ? 'د.إ' : '₹'})</Label>
                    <Controller
                      name="teamPrice"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          placeholder="299"
                          className="h-10"
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                 </div>
                </div>

                {/* Unlimited Tier */}
                <div className="p-4 border border-border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <h4 className="font-medium">Unlimited Tier</h4>
                  </div>
                  <div className="space-y-2">
                     <Label htmlFor="unlimitedPrice" className="text-sm">Price per month ({currency === 'USD' ? '$' : currency === 'AED' ? 'د.إ' : '₹'})</Label>
                    <Controller
                      name="unlimitedPrice"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          placeholder="999"
                          className="h-10"
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                 </div>
                </div>
              </div>
             </div>
           </div>
          ) : mainTab === 'people' ? (
            <JobsAndPeople />
          ) : (
            <CostBreakdown showHeader={false} revenueData={projectionData} />
          )}
      </div>

      {/* Collapsible Sidebar - Only show for calculator tab */}
      {mainTab === 'calculator' && isSidebarOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsSidebarOpen(false)}
          />

          {/* Sidebar */}
          <div className="fixed right-0 top-0 h-full w-full sm:w-[480px] lg:w-[560px] bg-background border-l border-border z-50 shadow-xl transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h2 className="text-xl font-semibold">Investment Calculator Settings</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-border">
                <button
                  onClick={() => setActiveTab('investment')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'investment'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Investment & Valuation
                </button>

                <button
                  onClick={() => setActiveTab('growth')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'growth'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Growth & Expenses
                </button>
                <button
                  onClick={() => setActiveTab('costs')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'costs'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Operating Costs
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'investment' && (
                  <div className="p-6 space-y-6">
                    <div className="space-y-3">
                      <h3 className="text-lg font-medium">Investment & Valuation</h3>
                      <p className="text-sm text-muted-foreground">
                        Set your investment terms and valuation parameters
                      </p>
                    </div>

                    {/* Valuation Type */}
                    <div className="space-y-3">
                      <Label htmlFor="valuationType" className="text-sm font-medium">
                        Valuation Preset
                      </Label>
                      <Controller
                        name="valuationType"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              handleValuationChange(value);
                            }}
                          >
                            <SelectTrigger className="h-11">
                              <SelectValue placeholder="Select valuation type" />
                            </SelectTrigger>
                             <SelectContent>
                               <SelectItem value="custom">Custom</SelectItem>

                               {/* Popular Accelerators & Programs */}
                               <SelectItem value="yc">Y Combinator (7% for 150k)</SelectItem>
                               <SelectItem value="500_startups">500 Startups (6% for 125k)</SelectItem>
                               <SelectItem value="techstars">Techstars (6% for 100k)</SelectItem>

                               {/* Popular Angel Networks */}
                               <SelectItem value="angelList">AngelList (10% for 100k)</SelectItem>
                               <SelectItem value="golden_gate_ventures">Golden Gate Ventures (8% for 250k)</SelectItem>

                               {/* Indian Angel Networks */}
                               <SelectItem value="indian_angel_network">Indian Angel Network (6% for ₹75L)</SelectItem>
                               <SelectItem value="Chennai_angels">Chennai Angels (5% for ₹50L)</SelectItem>

                               {/* Early Stage VC Firms */}
                               <SelectItem value="100x">100x.VC (8% for 500k)</SelectItem>
                               <SelectItem value="better_capital">Better Capital (12% for 3M)</SelectItem>
                               <SelectItem value="peak_xv">Peak XV (11% for 4M)</SelectItem>

                               {/* Indian Early Stage Funds */}
                               <SelectItem value="kalaari_capital">Kalaari Capital (12% for 3M)</SelectItem>
                               <SelectItem value="matrix_partners_india">Matrix Partners India (15% for 2.5M)</SelectItem>
                               <SelectItem value="india_quotient">India Quotient (11% for 3.5M)</SelectItem>
                               <SelectItem value="nexus_venture_partners">Nexus Venture Partners (13% for 2M)</SelectItem>

                               {/* US/European Early Stage Funds */}
                               <SelectItem value="benchmark">Benchmark (10% for 6M)</SelectItem>
                               <SelectItem value="balderton">Balderton Capital (11% for 7M)</SelectItem>

                               {/* Generic Seed Stages */}
                               <SelectItem value="seed">Seed Stage (20% for 500k)</SelectItem>
                               <SelectItem value="pre_seed">Pre-seed (15% for 250k)</SelectItem>
                             </SelectContent>
                          </Select>
                        )}
                      />
                      <p className="text-xs text-muted-foreground">
                        Choose a preset or select "Custom" to set your own values
                      </p>
                    </div>

                     {/* Investment Amount */}
                     <div className="space-y-3">
                       <Label htmlFor="investmentAmount" className="text-sm font-medium">
                         Investment Amount ({currency === 'USD' ? '$' : currency === 'AED' ? 'د.إ' : '₹'})
                       </Label>
                       <Controller
                         name="investmentAmount"
                         control={control}
                         render={({ field }) => (
                           <Input
                             {...field}
                             value={field.value ? convertFromINR(field.value) : ''}
                             type="number"
                             placeholder={currency === 'USD' ? '500000' : currency === 'AED' ? '1835000' : '40000000'}
                             className="h-11"
                             onChange={(e) => field.onChange(convertToINR(Number(e.target.value) || 0))}
                           />
                         )}
                       />
                       <p className="text-xs text-muted-foreground">
                         Total investment amount you're seeking
                       </p>
                     </div>

                    {/* Equity Percentage */}
                    <div className="space-y-3">
                      <Label htmlFor="equityPercentage" className="text-sm font-medium">
                        Equity Percentage (%)
                      </Label>
                      <Controller
                        name="equityPercentage"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="number"
                            placeholder="20"
                            className="h-11"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <p className="text-xs text-muted-foreground">
                        Percentage of company equity you're offering
                      </p>
                    </div>

                    {/* Revenue Multiple */}
                    <div className="space-y-3">
                      <Label htmlFor="revenueMultiple" className="text-sm font-medium">
                        Revenue Multiple
                      </Label>
                      <Controller
                        name="revenueMultiple"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="number"
                            step="0.1"
                            placeholder="8"
                            className="h-11"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <p className="text-xs text-muted-foreground">
                        Exit valuation as multiple of annual revenue
                      </p>
                    </div>
                  </div>
                )}



                {activeTab === 'growth' && (
                  <div className="p-6 space-y-6">
                    <div className="space-y-3">
                      <h3 className="text-lg font-medium">Growth & Operating Expenses</h3>
                      <p className="text-sm text-muted-foreground">
                        Configure user growth and cost projections
                      </p>
                    </div>

                    {/* Growth Settings */}
                    <div className="space-y-6">
                      <div className="p-4 bg-muted/30 rounded-lg space-y-4">
                        <h4 className="font-medium flex items-center gap-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          User Growth
                        </h4>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="initialUsers" className="text-sm">Initial Users</Label>
                            <Controller
                              name="initialUsers"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  placeholder="100"
                                  className="h-10"
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                              )}
                            />
                            <p className="text-xs text-muted-foreground">
                              Starting number of users at launch
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="monthlyGrowthRate" className="text-sm">Monthly Growth Rate (%)</Label>
                            <Controller
                              name="monthlyGrowthRate"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  step="0.1"
                                  placeholder="10"
                                  className="h-10"
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                              )}
                            />
                            <p className="text-xs text-muted-foreground">
                              Percentage increase in users each month
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Operating Expenses */}
                      <div className="p-4 bg-muted/30 rounded-lg space-y-4">
                        <h4 className="font-medium flex items-center gap-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          Operating Expenses
                        </h4>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="monthlyOpex" className="text-sm">Monthly OPEX (₹)</Label>
                            <Controller
                              name="monthlyOpex"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="number"
                                  placeholder="50000"
                                  className="h-10"
                                  onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                              )}
                            />
                             <p className="text-xs text-muted-foreground">
                               Base monthly operating expenses (excludes team salaries and detailed costs)
                             </p>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Controller
                              name="enableExpenseGrowth"
                              control={control}
                              render={({ field }) => (
                                <input
                                  type="checkbox"
                                  id="enableExpenseGrowth"
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                  className="rounded"
                                />
                              )}
                            />
                            <Label htmlFor="enableExpenseGrowth" className="text-sm">
                              Enable expense growth over time
                            </Label>
                          </div>

                          {formValues.enableExpenseGrowth && (
                            <div className="space-y-4 pl-6 border-l-2 border-muted">
                              <div className="space-y-2">
                                <Label htmlFor="expenseGrowthRate" className="text-sm">Expense Growth Rate (%)</Label>
                                <Controller
                                  name="expenseGrowthRate"
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="number"
                                      step="0.1"
                                      placeholder="10"
                                      className="h-10"
                                      onChange={(e) => field.onChange(Number(e.target.value))}
                                    />
                                  )}
                                />
                                <p className="text-xs text-muted-foreground">
                                  Monthly percentage increase in expenses
                                </p>
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="expenseGrowthCap" className="text-sm">Expense Growth Cap (% of Revenue)</Label>
                                <Controller
                                  name="expenseGrowthCap"
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="number"
                                      placeholder="50"
                                      className="h-10"
                                      onChange={(e) => field.onChange(Number(e.target.value))}
                                    />
                                  )}
                                />
                                <p className="text-xs text-muted-foreground">
                                  Maximum expenses as percentage of monthly revenue
                                </p>
                              </div>
                   </div>
                 )}

                  {activeTab === 'costs' && (
                   <div className="p-6 space-y-6">
                     <div className="space-y-3">
                       <h3 className="text-lg font-medium">Operating Costs Management</h3>
                       <p className="text-sm text-muted-foreground">
                         Manage your operating costs and infrastructure expenses
                       </p>
                     </div>

                     {/* Operating Costs Manager */}
                     <OperatingCostsManager planningMonths={state.planningMonths} />
                   </div>
                 )}
               </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Global Settings Modal */}
      {showGlobalSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Global Settings</h2>
                <Button
                  onClick={() => setShowGlobalSettings(false)}
                  variant="ghost"
                  size="sm"
                >
                  ✕
                </Button>
              </div>
              <GlobalSettings showHeader={false} />
              <div className="flex justify-end mt-6">
                <Button onClick={() => setShowGlobalSettings(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
